import { MAX_GUESSES, winPercent, type Profile, type Stats } from '@lexicon/core';
import { Sheet } from './Sheet';
import { SparkIcon } from './icons';
import { useStrings } from '../state/AppContext';

export interface StatsSheetProps {
  open: boolean;
  onClose: () => void;
  stats: Stats;
  profile: Profile;
}

function SummaryCell({ value, label }: { value: number; label: string }) {
  return (
    <div>
      <p className="text-xl font-bold">{value}</p>
      <p className="text-[10px] tracking-[0.14em] text-dim">{label}</p>
    </div>
  );
}

export function StatsSheet({ open, onClose, stats, profile }: StatsSheetProps) {
  const t = useStrings();
  const max = Math.max(...stats.distribution, 1);
  // Spec §10.1: MAX_GUESSES is the display *floor*. A player who has only
  // ever played the 5-letter daily still sees exactly six rows; rows 7 and
  // 8 appear only once a longer game has actually put something in them.
  let lastFilled = 0;
  stats.distribution.forEach((count, i) => { if (count > 0) lastFilled = i + 1; });
  const rows = Math.max(MAX_GUESSES, lastFilled);

  return (
    <Sheet open={open} title={t.common.statistics} onClose={onClose}>
      <div className="grid grid-cols-4 gap-2 text-center">
        <SummaryCell value={stats.played} label={t.stats.played} />
        <SummaryCell value={winPercent(stats)} label={t.stats.winPercent} />
        <SummaryCell value={profile.streak.currentStreak} label={t.stats.streak} />
        <SummaryCell value={profile.streak.maxStreak} label={t.stats.max} />
      </div>

      <div className="mt-4 flex items-center gap-2 text-sm">
        <SparkIcon className="h-4 w-4 text-accent" />
        <span className="text-dim">{t.stats.totalXpRow}</span>
        <span className="ml-auto font-semibold">{profile.totalXp}</span>
      </div>

      <div className="mt-4 pb-4">
        <p className="mb-2 text-xs font-semibold tracking-[0.14em] text-dim">{t.stats.distribution}</p>
        {stats.played === 0 ? (
          <p className="text-sm text-dim">{t.stats.noGames}</p>
        ) : (
          <ul aria-label={t.stats.distributionLabel} className="flex flex-col gap-1.5">
            {Array.from({ length: rows }, (_, i) => {
              const count = stats.distribution[i] ?? 0;
              const pct = max === 0 ? 0 : Math.round((count / max) * 100);
              return (
                <li
                  key={i}
                  aria-label={t.stats.distributionRow(i + 1, count)}
                  className="flex items-center gap-2 text-xs"
                >
                  <span className="w-3 text-dim">{i + 1}</span>
                  <span className="h-5 rounded bg-grad-correct" style={{ width: `${pct}%` }} />
                  <span className="text-dim">{count}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Sheet>
  );
}
